import { useQuery } from '@tanstack/react-query';

import cycleEvaluationGroupService, {
    type CycleEvaluationGroupDeletionImpact,
    type CycleEvaluationGroupReactivationImpact,
    type CycleEvaluationGroupReminderRecipients,
} from './cycleEvaluationGroupService';
import { partitionDormantCycleEvaluations } from './dormantCycleEvaluations';

import type { CycleEvaluationGroup } from '@/types/cycle-evaluation/CycleEvaluationGroup';

import { CycleEvaluationGroupQueryKey } from '@/constants/QueryKeys';

const LIST_QUERY_SCOPE = 'list';

const DELETION_IMPACT_QUERY_SCOPE = 'deletion-impact';

const REMINDER_RECIPIENTS_QUERY_SCOPE = 'reminder-recipients';

const REACTIVATION_IMPACT_QUERY_SCOPE = 'reactivation-impact';

export const useGetCycleEvaluationGroupList = (options?: { enabled?: boolean }) => {
    return useQuery<CycleEvaluationGroup[]>({
        queryKey: [CycleEvaluationGroupQueryKey.CYCLE_EVALUATION_GROUP, LIST_QUERY_SCOPE],
        queryFn: () => cycleEvaluationGroupService.getCycleEvaluationGroupList(),
        select: (groups) => groups.map(partitionDormantCycleEvaluations),
        enabled: options?.enabled ?? true,
    });
};

export const useGetCycleEvaluationGroupDeletionImpact = (id: string | undefined) => {
    return useQuery<CycleEvaluationGroupDeletionImpact>({
        queryKey: [
            CycleEvaluationGroupQueryKey.CYCLE_EVALUATION_GROUP,
            DELETION_IMPACT_QUERY_SCOPE,
            id,
        ],
        queryFn: () =>
            cycleEvaluationGroupService.getCycleEvaluationGroupDeletionImpact(id as string),
        enabled: Boolean(id),
    });
};

export const useGetCycleEvaluationGroupReminderRecipients = (id: string | undefined) => {
    return useQuery<CycleEvaluationGroupReminderRecipients>({
        queryKey: [
            CycleEvaluationGroupQueryKey.CYCLE_EVALUATION_GROUP,
            REMINDER_RECIPIENTS_QUERY_SCOPE,
            id,
        ],
        queryFn: () =>
            cycleEvaluationGroupService.getCycleEvaluationGroupReminderRecipients(id as string),
        enabled: Boolean(id),
    });
};

export const useGetCycleEvaluationGroupReactivationImpact = (id: string | undefined) => {
    return useQuery<CycleEvaluationGroupReactivationImpact>({
        queryKey: [
            CycleEvaluationGroupQueryKey.CYCLE_EVALUATION_GROUP,
            REACTIVATION_IMPACT_QUERY_SCOPE,
            id,
        ],
        queryFn: () =>
            cycleEvaluationGroupService.getCycleEvaluationGroupReactivationImpact(id as string),
        enabled: Boolean(id),
    });
};
